import { NavLink, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { ENTITIES, ENTITY_GROUPS, findEntity } from '@/config/masterData';
import { EntityManager } from '@/components/crud/EntityManager';
import { SettingsPage } from './admin/SettingsPage';
import { UsersPage } from './admin/UsersPage';
import { BranchesPage } from './admin/BranchesPage';
import { NotificationsPage } from './admin/NotificationsPage';
import { AuditPage } from './admin/AuditPage';
import { DemoDataPage } from './admin/DemoDataPage';
import { ResetPage } from './admin/ResetPage';
import { FuelSyncPage } from './admin/FuelSyncPage';
import { Panel } from '@/components/ui/Panel';
import { useAuthStore } from '@/stores/authStore';

/** Administration shell. Master data screens are generated from the
 *  descriptors in config/masterData.ts, so adding a lookup table here is
 *  a config entry rather than a new page. */
export function Admin() {
  const isAdmin = useAuthStore((s) => s.hasRole('admin'));
  return (
    <div className="admin">
      <Panel flush className="admin__nav">
        {ENTITY_GROUPS.map((group) => (
          <div key={group}>
            <div className="admin__group">{group}</div>
            {ENTITIES.filter((e) => e.group === group).map((e) => (
              <NavLink
                key={e.key}
                to={`/admin/${e.key}`}
                className={({ isActive }) => `admin__link${isActive ? ' is-active' : ''}`}
              >
                {e.label}
              </NavLink>
            ))}
          </div>
        ))}

        <div className="admin__group">System</div>
        <NavLink to="/admin/settings" className={({ isActive }) => `admin__link${isActive ? ' is-active' : ''}`}>
          Settings
        </NavLink>
        <NavLink to="/admin/notifications" className={({ isActive }) => `admin__link${isActive ? ' is-active' : ''}`}>
          Report delivery
        </NavLink>
        <NavLink to="/admin/users" className={({ isActive }) => `admin__link${isActive ? ' is-active' : ''}`}>
          Users &amp; roles
        </NavLink>
        <NavLink to="/admin/branches" className={({ isActive }) => `admin__link${isActive ? ' is-active' : ''}`}>
          Branches
        </NavLink>
        <NavLink to="/admin/audit" className={({ isActive }) => `admin__link${isActive ? ' is-active' : ''}`}>
          Audit trail
        </NavLink>
        {isAdmin && (
          <NavLink to="/admin/fuel-sync" className={({ isActive }) => `admin__link${isActive ? ' is-active' : ''}`}>
            Fuel sync
          </NavLink>
        )}
        {isAdmin && (
          <NavLink to="/admin/demo-data" className={({ isActive }) => `admin__link${isActive ? ' is-active' : ''}`}>
            Demo data
          </NavLink>
        )}
        {isAdmin && (
          <NavLink to="/admin/reset" className={({ isActive }) => `admin__link${isActive ? ' is-active' : ''}`}>
            Reset system
          </NavLink>
        )}
      </Panel>

      <div style={{ minWidth: 0 }}>
        <Routes>
          <Route index element={<Navigate to="settings" replace />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="branches" element={<BranchesPage />} />
          <Route path="audit" element={<AuditPage />} />
          <Route path="fuel-sync" element={<FuelSyncPage />} />
          <Route path="demo-data" element={<DemoDataPage />} />
          <Route path="reset" element={<ResetPage />} />
          <Route path=":entity" element={<EntityRoute />} />
        </Routes>
      </div>
    </div>
  );
}

function EntityRoute() {
  const { entity: key } = useParams();
  const entity = findEntity(key ?? '');

  if (!entity) {
    return (
      <div className="placeholder">
        <h2>Unknown section</h2>
        <p className="muted">Pick a section from the list.</p>
      </div>
    );
  }
  return <EntityManager key={entity.key} entity={entity} />;
}
